import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Mail, Lock, KeyRound, User } from 'lucide-react';

/**
 * Reusable account settings block for admin/staff/driver panels.
 * - Change login email (password-confirmed; old email is invalidated server-side)
 * - Change password
 * - Forgot-password link (deep-link to /login with modal open via query param)
 */
export function AccountSettings({ className = '' }) {
  const { user, refreshSession } = useAuth();
  const [email, setEmail] = useState(user?.email || '');
  const [currentPwdForEmail, setCurrentPwdForEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [savingPwd, setSavingPwd] = useState(false);

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email.trim() || !currentPwdForEmail) return toast.error('Enter both fields');
    if (email.trim().toLowerCase() === (user?.email || '').toLowerCase()) return toast.error('New email is the same as current');
    setSavingEmail(true);
    try {
      const res = await api.put('/auth/change-email', { current_password: currentPwdForEmail, new_email: email.trim() });
      toast.success('Login email updated');
      if (res.data?.token) {
        await refreshSession(res.data.token);
      }
      setCurrentPwdForEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!currentPwd || !newPwd) return toast.error('Fill all fields');
    if (newPwd.length < 6) return toast.error('New password must be at least 6 characters');
    if (newPwd !== confirmPwd) return toast.error('New passwords do not match');
    setSavingPwd(true);
    try {
      await api.put('/auth/change-password', { current_password: currentPwd, new_password: newPwd });
      toast.success('Password changed');
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className={`space-y-4 ${className}`} data-testid="account-settings">
      {/* Identity card */}
      <div className="bg-[#0B0E14] border border-white/5 rounded-lg p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-[#b56b38]/20 flex items-center justify-center"><User className="w-5 h-5 text-[#b56b38]" /></div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium">{user?.name || 'User'}</p>
          <p className="text-[#64748B] text-xs truncate">{user?.email || user?.phone || ''} · <span className="uppercase">{user?.role}</span></p>
        </div>
      </div>

      {/* Change email */}
      {user?.email && (
        <form onSubmit={submitEmail} className="bg-[#12151C] border border-white/10 rounded-xl p-4 space-y-3" data-testid="change-email-form">
          <h4 className="text-white font-medium flex items-center gap-2"><Mail className="w-4 h-4 text-[#b56b38]" /> Change Login Email</h4>
          <p className="text-[#64748B] text-xs">After saving, the old email will no longer work — use the new email to log in next time.</p>
          <div>
            <label className="text-[#94A3B8] text-xs mb-1 block">New Email</label>
            <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="you@example.com" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-[#64748B]" data-testid="new-email-input" />
          </div>
          <div>
            <label className="text-[#94A3B8] text-xs mb-1 block">Current Password</label>
            <input value={currentPwdForEmail} onChange={e => setCurrentPwdForEmail(e.target.value)} type="password" placeholder="Confirm with your password" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-[#64748B]" data-testid="email-current-password-input" />
          </div>
          <button type="submit" disabled={savingEmail} className="w-full bg-[#cc3737] hover:bg-[#d94444] disabled:opacity-40 text-white rounded-full py-2 text-sm font-medium" data-testid="save-email-btn">{savingEmail ? 'Saving...' : 'Update Login Email'}</button>
        </form>
      )}

      {/* Change password */}
      <form onSubmit={submitPassword} className="bg-[#12151C] border border-white/10 rounded-xl p-4 space-y-3" data-testid="change-password-form">
        <h4 className="text-white font-medium flex items-center gap-2"><Lock className="w-4 h-4 text-[#b56b38]" /> Change Password</h4>
        <div>
          <label className="text-[#94A3B8] text-xs mb-1 block">Current Password</label>
          <input value={currentPwd} onChange={e => setCurrentPwd(e.target.value)} type="password" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="current-password-input" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[#94A3B8] text-xs mb-1 block">New Password</label>
            <input value={newPwd} onChange={e => setNewPwd(e.target.value)} type="password" placeholder="Min 6 characters" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="new-password-input" />
          </div>
          <div>
            <label className="text-[#94A3B8] text-xs mb-1 block">Confirm New Password</label>
            <input value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} type="password" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="confirm-password-input" />
          </div>
        </div>
        <button type="submit" disabled={savingPwd} className="w-full bg-[#cc3737] hover:bg-[#d94444] disabled:opacity-40 text-white rounded-full py-2 text-sm font-medium" data-testid="save-password-btn">{savingPwd ? 'Saving...' : 'Change Password'}</button>
      </form>

      {/* Forgot password link */}
      <div className="bg-[#12151C] border border-white/10 rounded-xl p-4" data-testid="forgot-password-link-box">
        <h4 className="text-white font-medium flex items-center gap-2 mb-2"><KeyRound className="w-4 h-4 text-[#b56b38]" /> Forgot your password?</h4>
        <p className="text-[#94A3B8] text-xs mb-3">If you're locked out of your account, request a one-time reset code. The code is delivered via SMS (when Twilio is configured) or printed to the server log for the admin to relay.</p>
        <a href="/login?forgot=1" className="inline-flex items-center gap-2 bg-[#0B0E14] border border-[#b56b38]/40 text-[#b56b38] hover:bg-[#b56b38]/10 rounded-full px-4 py-2 text-sm font-medium transition-colors" data-testid="forgot-password-link">
          <KeyRound className="w-4 h-4" /> Reset via code
        </a>
      </div>
    </div>
  );
}

export default AccountSettings;
